import { useEffect, useRef } from "react";
import { formatEther } from "viem";
import { COLORS } from "../lib/contracts";

type Props = {
  spinning: boolean;
  /** Winning position — the slot GREEN lands on when the wheel stops. */
  targetIndex: number | null;
  onSpinEnd: () => void;
  highlightWin: boolean;
  /** Positions you have selected for this round (equal stake on each). */
  selected: boolean[];
  /** Chips stacked on every selected position (uniform). */
  stackPerColor: number;
  /** Stake per selected position, in wei — shown in the arrow tooltip. */
  stakePerColor: bigint;
  /** True when the 5-position limit is reached (unselected positions lock). */
  selectionFull: boolean;
  /** Everyone's on-chain stake per position this round. */
  colorTotals?: bigint[];
  /** Your on-chain stake per position this round. */
  myStakes?: bigint[];
  onSegmentClick?: (position: number) => void;
  interactive: boolean;
};

const SEGMENT = 360 / COLORS.length;
const SPIN_SECONDS = 7;
const R_RIM_OUTER = 200;
const R_RIM_INNER = 178;
const R_SEG_OUTER = 176;
// slices run almost to the middle — no central hub/arrow anymore
const R_SEG_INNER = 12;
const BULB_COUNT = 24;
const LIME_HEX = COLORS[0].hex; // green — the win colour
const GREEN_SEGMENT = 0; // the disc slice that lands on the winning position

function polar(r: number, angleDeg: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180; // 0° = top, clockwise
  return [r * Math.cos(rad), r * Math.sin(rad)];
}

function wedgePath(a0: number, a1: number, r0: number, r1: number) {
  const [x0o, y0o] = polar(r1, a0);
  const [x1o, y1o] = polar(r1, a1);
  const [x1i, y1i] = polar(r0, a1);
  const [x0i, y0i] = polar(r0, a0);
  return [
    `M ${x0o.toFixed(2)} ${y0o.toFixed(2)}`,
    `A ${r1} ${r1} 0 0 1 ${x1o.toFixed(2)} ${y1o.toFixed(2)}`,
    `L ${x1i.toFixed(2)} ${y1i.toFixed(2)}`,
    `A ${r0} ${r0} 0 0 0 ${x0i.toFixed(2)} ${y0i.toFixed(2)}`,
    "Z",
  ].join(" ");
}

function shortEth(v: bigint) {
  const n = Number(formatEther(v));
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(3);
  return n.toFixed(4).replace(/^0/, "");
}

export function ColorWheel({
  spinning,
  targetIndex,
  onSpinEnd,
  highlightWin,
  selected,
  stackPerColor,
  stakePerColor,
  selectionFull,
  colorTotals,
  myStakes,
  onSegmentClick,
  interactive,
}: Props) {
  // Only the coloured disc spins now — there's no central arrow. It stops so the
  // GREEN slice lands on `targetIndex`: whoever placed an arrow on that position
  // wins. The numbered betting track + arrows stay fixed on top.
  const discRef = useRef<HTMLDivElement>(null);
  const discRot = useRef(0);
  const startedRef = useRef(false);
  const deadlineRef = useRef(0);

  useEffect(() => {
    const disc = discRef.current;
    if (!disc) return;
    if (!spinning || targetIndex === null) {
      startedRef.current = false;
      return;
    }

    if (!startedRef.current) {
      startedRef.current = true;

      // Disc stops so its green slice sits under the winning position. Slice s
      // sits under screen angle A when rotation D satisfies (A − D) inside s.
      const discTarget =
        ((((targetIndex - GREEN_SEGMENT) * SEGMENT) % 360) + 360) % 360;
      const dBase = ((discRot.current % 360) + 360) % 360;
      const dDelta = (dBase - discTarget + 360) % 360;
      const dTurns = 5 + Math.floor(Math.random() * 3);
      discRot.current = discRot.current - dTurns * 360 - dDelta;

      const curve = `cubic-bezier(0.12, 0.75, 0.08, 1)`;
      disc.style.transition = "none";
      void disc.offsetWidth;
      disc.style.transition = `transform ${SPIN_SECONDS}s ${curve}`;
      disc.style.transform = `rotate(${discRot.current}deg)`;
      deadlineRef.current = performance.now() + SPIN_SECONDS * 1000 + 400;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      startedRef.current = false;
      onSpinEnd();
    };
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "transform" && e.target === disc) finish();
    };
    disc.addEventListener("transitionend", onTransitionEnd);
    const timer = window.setTimeout(
      finish,
      Math.max(200, deadlineRef.current - performance.now())
    );

    return () => {
      disc.removeEventListener("transitionend", onTransitionEnd);
      window.clearTimeout(timer);
    };
  }, [spinning, targetIndex, onSpinEnd]);

  const bulbRadius = (R_RIM_OUTER + R_RIM_INNER) / 2;
  const bulbs = Array.from({ length: BULB_COUNT }, (_, i) => {
    const [x, y] = polar(bulbRadius, (i * 360) / BULB_COUNT);
    return (
      <g key={i} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)})`}>
        <circle
          className={`bulb-halo ${spinning ? "chasing" : "twinkle"}`}
          r="10"
          fill="rgba(255, 233, 168, 0.35)"
          style={{ animationDelay: `${(i % 6) * 0.12}s` }}
        />
        <circle
          className={`bulb ${spinning ? "chasing" : "twinkle"}`}
          r="4.8"
          fill="url(#bulbFill)"
          stroke="#8a6a1c"
          strokeWidth="0.9"
          style={{ animationDelay: `${(i % 6) * 0.12}s` }}
        />
      </g>
    );
  });

  return (
    <div className={`wheel-shell ${highlightWin ? "won" : ""} ${spinning ? "spinning" : ""}`}>
      {/* spinning layer: the coloured disc (with rim + bulbs riding along) */}
      <div className="wheel-spinner" ref={discRef}>
        <svg className="wheel-svg" viewBox="-252 -252 504 504" role="img" aria-label="Color wheel">
          <defs>
            <linearGradient id="goldRim" x1="0" y1="-1" x2="0" y2="1">
              <stop offset="0%" stopColor="#fbe89a" />
              <stop offset="28%" stopColor="#d4af37" />
              <stop offset="55%" stopColor="#8a6a1c" />
              <stop offset="78%" stopColor="#d4af37" />
              <stop offset="100%" stopColor="#6e5312" />
            </linearGradient>
            <linearGradient id="goldEdge" x1="0" y1="-1" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff6cf" />
              <stop offset="100%" stopColor="#a8842a" />
            </linearGradient>
            <radialGradient id="bulbFill" cx="38%" cy="34%" r="80%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="45%" stopColor="#ffe9a8" />
              <stop offset="100%" stopColor="#d49a1e" />
            </radialGradient>
          </defs>

          <circle r={R_RIM_OUTER} fill="url(#goldRim)" stroke="#5c4610" strokeWidth="2.5" />
          <circle
            r={R_RIM_OUTER - 2.5}
            fill="none"
            stroke="url(#goldEdge)"
            strokeWidth="1.6"
            opacity="0.9"
          />
          <circle r={R_RIM_INNER} fill="#151005" stroke="#6e5312" strokeWidth="2" />
          {bulbs}

          {COLORS.map((c, i) => (
            <path
              key={c.name}
              className="segment-face"
              d={wedgePath(i * SEGMENT, (i + 1) * SEGMENT, R_SEG_INNER, R_SEG_OUTER)}
              fill={c.hex}
            />
          ))}
          {COLORS.map((_, i) => {
            const [x0, y0] = polar(R_SEG_INNER, i * SEGMENT);
            const [x1, y1] = polar(R_SEG_OUTER, i * SEGMENT);
            return (
              <line
                key={i}
                x1={x0.toFixed(2)}
                y1={y0.toFixed(2)}
                x2={x1.toFixed(2)}
                y2={y1.toFixed(2)}
                stroke="url(#goldEdge)"
                strokeWidth="2.2"
                opacity="0.85"
              />
            );
          })}
        </svg>
      </div>

      {/* static layer: sheen, numbered betting track + your placed arrows */}
      <svg className="wheel-overlay" viewBox="-252 -252 504 504">
        <defs>
          <radialGradient id="segSheen" cx="50%" cy="32%" r="72%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
            <stop offset="45%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="70%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.22)" />
          </radialGradient>
          <filter id="chipShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2.2" stdDeviation="2.4" floodColor="#000" floodOpacity="0.6" />
          </filter>
        </defs>

        <circle r={R_SEG_OUTER} fill="url(#segSheen)" pointerEvents="none" />

        {COLORS.map((_, i) => {
          const a0 = i * SEGMENT;
          const a1 = (i + 1) * SEGMENT;
          const mid = a0 + SEGMENT / 2;
          const isSelected = selected[i] ?? false;
          const mine = myStakes?.[i] ?? 0n;
          const everyone = colorTotals?.[i] ?? 0n;
          const locked = selectionFull && !isSelected && mine === 0n;
          const aimed = isSelected || mine > 0n;
          const isResult = !spinning && targetIndex === i;
          const [px, py] = polar(228, mid);
          const [t0x, t0y] = polar(210, mid);
          return (
            <g
              key={i}
              className={`pos ${interactive && !locked ? "clickable" : ""} ${
                aimed ? "selected" : ""
              } ${mine > 0n ? "staked" : ""} ${locked ? "locked" : ""} ${
                isResult ? "result" : ""
              }`}
              onClick={() => {
                if (interactive && !locked && onSegmentClick) onSegmentClick(i);
              }}
            >
              <title>
                {mine > 0n
                  ? `P${i + 1} — ${shortEth(mine)} ETH locked in`
                  : isSelected
                    ? `P${i + 1} — ${
                        stakePerColor > 0n ? `${shortEth(stakePerColor)} ETH` : "aimed"
                      }${stackPerColor > 1 ? ` (×${stackPerColor})` : ""}`
                    : `aim your arrow at position ${i + 1}`}
              </title>

              <path className="pos-hit" d={wedgePath(a0, a1, R_SEG_INNER, R_SEG_OUTER)} />

              <line
                className="pos-tick"
                x1={t0x.toFixed(2)}
                y1={t0y.toFixed(2)}
                x2={(px * 0.93).toFixed(2)}
                y2={(py * 0.93).toFixed(2)}
              />

              {/* the classic wooden arrow, now planted on each position you pick */}
              {aimed && (
                <g
                  className={`seg-arrow ${isResult && highlightWin ? "hit" : ""}`}
                  transform={`rotate(${mid}) translate(0 -34) scale(0.72)`}
                  filter="url(#chipShadow)"
                >
                  <rect x="-3" y="-140" width="6" height="140" rx="3" fill="#b06f2a" stroke="#5e3a12" strokeWidth="1.2" />
                  <path d="M 0 -172 L 13 -126 L 4.4 -134 L -4.4 -134 L -13 -126 Z" fill="#4a4a4f" stroke="#2b2b2e" strokeWidth="1.4" />
                  <rect x="-3.8" y="-50" width="7.6" height="4.5" rx="1.6" fill="#6b6b70" stroke="#2b2b2e" strokeWidth="0.8" />
                  <path d="M 3 -46 L 16 -34 L 16 -8 L 3 -18 Z" fill="#a9611f" stroke="#5e3a12" strokeWidth="1" />
                  <path d="M -3 -46 L -16 -34 L -16 -8 L -3 -18 Z" fill="#c07a2c" stroke="#5e3a12" strokeWidth="1" />
                  <path d="M 6 -38 L 13 -31 M 6 -30 L 13 -23 M 6 -22 L 13 -16" stroke="#5e3a12" strokeWidth="1" opacity="0.6" />
                  <path d="M -6 -38 L -13 -31 M -6 -30 L -13 -23 M -6 -22 L -13 -16" stroke="#5e3a12" strokeWidth="1" opacity="0.6" />
                </g>
              )}

              <g transform={`translate(${px.toFixed(2)} ${py.toFixed(2)})`} filter="url(#chipShadow)">
                <circle className="pos-badge" r="16" strokeDasharray="6.8 4.6" />
                <circle className="pos-badge-rim" r="16" />
                <text className="pos-num" textAnchor="middle" dy="4.5">
                  {i + 1}
                </text>
              </g>
              {everyone > 0n && (
                <text
                  className="segment-total"
                  transform={`translate(${(px * 0.86).toFixed(2)} ${(py * 0.86).toFixed(2)})`}
                  textAnchor="middle"
                  dy="3"
                >
                  {shortEth(everyone)}Ξ
                </text>
              )}
            </g>
          );
        })}

        {/* GREEN landed here — the winning position */}
        {!spinning && targetIndex !== null && (
          <circle
            className={`pos-result ${highlightWin ? "hit" : ""}`}
            cx={polar(228, targetIndex * SEGMENT + SEGMENT / 2)[0]}
            cy={polar(228, targetIndex * SEGMENT + SEGMENT / 2)[1]}
            r="21"
            fill="none"
            stroke={LIME_HEX}
          />
        )}
      </svg>
    </div>
  );
}
